import { useEffect, useRef } from 'react'

/**
 * useRingtone
 *
 * Plays a gentle, repeating phone-like ring tone using the Web Audio API.
 * No external asset is required. The tone consists of two short sine beeps
 * at 440 Hz / 480 Hz followed by silence, repeating every 3 seconds –
 * reminiscent of a classic telephone ring cadence while remaining subtle.
 *
 * Browsers may block autoplay until the user has interacted with the page.
 * In practice the doctor has always logged in before a call arrives, so this
 * is usually not an issue. If autoplay is blocked, start() silently fails.
 */
export const useRingtone = (enabled: boolean): void => {
  const ctxRef = useRef<AudioContext | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const activeNodesRef = useRef<Array<{ osc: OscillatorNode; gain: GainNode }>>([])

  useEffect(() => {
    if (!enabled) return undefined

    const AudioCtor =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioCtor) return undefined

    let ctx: AudioContext
    try {
      ctx = new AudioCtor()
      ctxRef.current = ctx
    } catch (err) {
      return undefined
    }

    const playRing = (): void => {
      if (!ctxRef.current || ctxRef.current.state === 'closed') return

      // Some browsers suspend the audio context until a user gesture.
      if (ctxRef.current.state === 'suspended') {
        ctxRef.current.resume().catch(() => {
          /* best-effort */
        })
      }

      const now = ctxRef.current.currentTime
      const ringDuration = 1.2 // seconds: two short tones

      const schedule = (startOffset: number, duration: number): void => {
        const osc = ctxRef.current!.createOscillator()
        const gain = ctxRef.current!.createGain()
        osc.type = 'sine'
        // Classic ringback: 440 Hz + 480 Hz blended for a richer tone
        osc.frequency.setValueAtTime(440, now + startOffset)

        // Soft envelope so there are no clicks
        gain.gain.setValueAtTime(0, now + startOffset)
        gain.gain.linearRampToValueAtTime(0.18, now + startOffset + 0.02)
        gain.gain.setValueAtTime(0.18, now + startOffset + duration - 0.05)
        gain.gain.linearRampToValueAtTime(0, now + startOffset + duration)

        osc.connect(gain)
        gain.connect(ctxRef.current!.destination)
        osc.start(now + startOffset)
        osc.stop(now + startOffset + duration)

        activeNodesRef.current.push({ osc, gain })

        osc.onended = () => {
          activeNodesRef.current = activeNodesRef.current.filter(node => node.osc !== osc)
          try {
            gain.disconnect()
          } catch {
            /* ignore */
          }
        }
      }

      // Two short beeps with a small gap – mimics a phone ring cadence
      schedule(0, ringDuration / 2 - 0.05)
      schedule(ringDuration / 2 + 0.05, ringDuration / 2 - 0.05)
    }

    // Ring immediately, then repeat every 3 seconds
    playRing()
    intervalRef.current = setInterval(playRing, 3000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      // Stop any currently-playing nodes
      activeNodesRef.current.forEach(({ osc, gain }) => {
        try {
          osc.stop()
        } catch {
          /* ignore */
        }
        try {
          gain.disconnect()
        } catch {
          /* ignore */
        }
      })
      activeNodesRef.current = []
      if (ctxRef.current && ctxRef.current.state !== 'closed') {
        ctxRef.current.close().catch(() => {
          /* best-effort */
        })
      }
      ctxRef.current = null
    }
  }, [enabled])
}

export default useRingtone
